import type { ReportColumn } from '../models/column.types'
import type { ReportViewConfig, UpsertReportViewConfig } from '../models/reportViewConfig.types'
import type { ReportTablePreferenceV1, ReportTableSortV1 } from '../models/tableConfig.types'

export {
  parseReportViewConfig,
  parseReportViewConfigs,
  parseReportViewConfigUpsertAck,
  serializeReportViewConfig,
} from '../models/reportViewConfig.wire'

export function parseReportViewSort(
  field: string | null,
  order: string | number | null,
): ReportTableSortV1 | null {
  if (!field) return null
  if (order === 1 || order === '1' || order === 'asc') return { field, order: 1 }
  if (order === -1 || order === '-1' || order === 'desc') return { field, order: -1 }
  return null
}

/** Convert remote selections into the local presentation preference without inventing widths. */
export function reportViewConfigToTablePreference(
  config: ReportViewConfig,
): ReportTablePreferenceV1 {
  const seen = new Set<string>()
  const media = Array.isArray(config.mediaColumns) ? config.mediaColumns : []
  const columnOrder = [...config.dimensions, ...config.metrics, ...media].filter((field) => {
    if (seen.has(field)) return false
    seen.add(field)
    return true
  })
  return {
    version: 1,
    columnOrder,
    columnWidths: {},
    sort: parseReportViewSort(config.sortField, config.sortOrder),
  }
}

export interface BuildReportViewConfigInput {
  workspaceId: number | string
  tableId: string
  columns: readonly ReportColumn[]
  sort: ReportTableSortV1 | null
  breakdownColumn?: string | null
  activeChart?: string | null
}

/** Build the existing backend upsert from the current visible, ordered report presentation. */
export function buildReportViewConfigUpsert(
  input: BuildReportViewConfigInput,
): UpsertReportViewConfig {
  const seen = new Set<string>()
  const visible = input.columns.filter((column) => {
    if (!column.field || column.invisible || column.selected === false || seen.has(column.field)) {
      return false
    }
    seen.add(column.field)
    return true
  })
  const mediaColumns = visible
    .filter((column) => column.isImage || column.sub_type === 'image')
    .map((column) => column.field)
  const dataColumns = visible.filter((column) => !column.isImage && column.sub_type !== 'image')
  return {
    workspaceId: input.workspaceId,
    tableId: input.tableId,
    dimensions: dataColumns.filter((column) => !column.numeric).map((column) => column.field),
    metrics: dataColumns.filter((column) => column.numeric).map((column) => column.field),
    mediaColumns,
    sortField: input.sort?.field ?? null,
    sortOrder: input.sort ? String(input.sort.order) : null,
    breakdownColumn: input.breakdownColumn ?? null,
    activeChart: input.activeChart ?? null,
  }
}
